import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/services/api";

// Pages
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import PolicyBuilder from "@/pages/PolicyBuilder";
import Templates from "@/pages/Templates";
import AdminDashboard from "@/pages/AdminDashboard";
import { TermsPage, PrivacyPage } from "@/pages/Legal";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import ApiDocs from "@/pages/ApiDocs";
import WebhookManager from "@/pages/WebhookManager";
import AdvancedAnalytics from "@/pages/AdvancedAnalytics";
import ClaimsManager from "@/pages/ClaimsManager";
import PlanComparator from "@/pages/PlanComparator";
import QuoteCalculator from "@/pages/QuoteCalculator";
import LeadsDashboard from "@/pages/LeadsDashboard";
import KycVerification from "@/pages/KycVerification";
import KycAdminReview from "@/pages/KycAdminReview";
import BrandingAdmin from "@/pages/BrandingAdmin";
import CarrierMarketplace from "@/pages/CarrierMarketplace";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-[#3D5A80]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/builder" replace />;
  }

  return children;
};

// Seed database on first load
const SeedDatabase = () => {
  useEffect(() => {
    api.seedDatabase().catch(() => {});
  }, []);
  return null;
};

// Force page title (overrides external scripts)
const TitleEnforcer = () => {
  useEffect(() => {
    const setTitle = () => { document.title = 'InsureLab | AI Insurance Builder'; };
    setTitle();
    const observer = new MutationObserver(setTitle);
    const titleEl = document.querySelector('title');
    if (titleEl) observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    const interval = setInterval(setTitle, 2000);
    return () => { observer.disconnect(); clearInterval(interval); };
  }, []);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SeedDatabase />
        <TitleEnforcer />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/developers" element={<ApiDocs />} />
          <Route path="/compare" element={<PlanComparator />} />
          <Route path="/quote" element={<QuoteCalculator />} />
          
          {/* Protected Routes */}
          <Route path="/builder" element={
            <ProtectedRoute>
              <PolicyBuilder />
            </ProtectedRoute>
          } />
          <Route path="/checkout/success" element={
            <ProtectedRoute>
              <CheckoutSuccess />
            </ProtectedRoute>
          } />
          <Route path="/claims" element={
            <ProtectedRoute>
              <ClaimsManager />
            </ProtectedRoute>
          } />
          <Route path="/kyc" element={
            <ProtectedRoute>
              <KycVerification />
            </ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/webhooks" element={
            <ProtectedRoute adminOnly>
              <WebhookManager />
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute adminOnly>
              <AdvancedAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin/leads" element={
            <ProtectedRoute adminOnly>
              <LeadsDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/kyc" element={
            <ProtectedRoute adminOnly>
              <KycAdminReview />
            </ProtectedRoute>
          } />
          <Route path="/admin/branding" element={
            <ProtectedRoute adminOnly>
              <BrandingAdmin />
            </ProtectedRoute>
          } />
          <Route path="/carriers" element={
            <ProtectedRoute>
              <CarrierMarketplace />
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
